/**
 * The GitHub workflow kit for a project: CI that publishes an image per commit, a deploy workflow for each of
 * its environments, a setup checklist and a prompt for an AI agent. Rendered from the project's own settings,
 * served as a ZIP in project settings, through the agent API and by the plugin CLI. This is the only copy of the
 * templates.
 *
 * The contract every deploy workflow keeps: Jira dispatches it on the default branch with three inputs, the run
 * names itself `Deploy REL-n ATT-n <revision>` so the webhook can map it back to the attempt, its first step is
 * the jira-gate action, it deploys exactly `inputs.revision`, and its result is the attempt's outcome.
 */

/** The run name every deploy workflow must set: Jira links the finished run to its attempt by it. */
export const DEPLOY_RUN_NAME = 'Deploy ${{ inputs.release }} ${{ inputs.attempt }} ${{ inputs.revision }}';

const normalizeExpression = (value: string) =>
  value
    .trim()
    .replace(/^(['"])(.*)\1$/, '$2')
    .replace(/\$\{\{\s*([^}]*?)\s*\}\}/g, '${{ $1 }}')
    .replace(/\s+/g, ' ');

/**
 * Why a deploy workflow's run name breaks the contract, or null when it keeps it (JIRA-20). Without the run name
 * every run is titled after the workflow and Jira cannot record its result.
 */
export function deployRunNameProblem(workflowYaml: string): string | null {
  const line = /^run-name:[ \t]*(.*?)[ \t]*(?:#.*)?$/m.exec(workflowYaml);
  if (!line)
    return `The workflow has no run-name, so Jira cannot link its runs. Add: run-name: ${DEPLOY_RUN_NAME}`;
  if (normalizeExpression(line[1]!) !== DEPLOY_RUN_NAME)
    return `The workflow's run-name is "${line[1]!.trim()}", but Jira links runs named: ${DEPLOY_RUN_NAME}`;
  return null;
}

/** The gate step's action; other repositories need access to it (Settings → Actions → General → Access). */
export const GATE_ACTION = 'lixionary/lixionary-jira/.github/actions/jira-gate@main';

/** The CI job whose success means the revision's image exists; environments should require it. */
export const IMAGE_CHECK = 'images';

/** A GitHub environment and concurrency name for a Jira environment name. */
export const environmentSlug = (name: string) =>
  name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'production';

const HEADER = (title: string, slug: string) =>
  `name: ${JSON.stringify(title)}
# Lixionary Jira dispatches this workflow on the default branch when a release proceeds. Keep the inputs and the
# run name: the GitHub webhook maps the finished run back to the attempt by its name, and the run's result becomes
# the attempt's outcome.
run-name: ${DEPLOY_RUN_NAME}

on:
  workflow_dispatch:
    inputs:
      revision:
        description: Full commit SHA on the default branch to deploy
        required: true
        type: string
      release:
        description: Lixionary Jira release, e.g. REL-5
        required: true
        type: string
      attempt:
        description: Deployment attempt, e.g. ATT-1
        required: true
        type: string

permissions:
  contents: read

concurrency:
  group: deploy-${slug}
  cancel-in-progress: false
`;

const gateStep = (environment: string, withArtifact: boolean) => {
  const artifact = withArtifact ? '\n          artifact: ${{ steps.image.outputs.ref }}' : '';
  return `      # Refuses anything Jira did not start: a manual dispatch, a finished attempt, another revision.
      - name: Check the attempt with Lixionary Jira
        uses: ${GATE_ACTION}
        with:
          release: \${{ inputs.release }}
          attempt: \${{ inputs.attempt }}
          revision: \${{ inputs.revision }}
          project: \${{ vars.JIRA_PROJECT }}
          environment: ${JSON.stringify(environment)}${artifact}
          jira-url: \${{ vars.LIXIONARY_JIRA_URL }}
          client-id: \${{ secrets.LIXIONARY_JIRA_CLIENT_ID }}
          client-secret: \${{ secrets.LIXIONARY_JIRA_CLIENT_SECRET }}
`;
};

const minimalJob = (environment: string, slug: string) =>
  `
jobs:
  deploy:
    runs-on: ubuntu-24.04
    timeout-minutes: 20
    environment: ${slug}
    steps:
${gateStep(environment, false)}      - uses: actions/checkout@v4
        with:
          ref: \${{ inputs.revision }}
      # Replace with the deployment. Pass inputs through env, never by interpolating them into \`run\`, and exit
      # non-zero on failure: the run's result is the attempt's outcome.
      - name: Deploy
        env:
          REVISION: \${{ inputs.revision }}
        run: ./scripts/deploy.sh "$REVISION"
`;

const dockerJob = (environment: string, slug: string) =>
  `
jobs:
  deploy:
    runs-on: ubuntu-24.04
    timeout-minutes: 20
    # Holds this environment's variables and secrets. Avoid required reviewers here: Jira already gated the
    # release, and a run waiting for a reviewer for over an hour marks the attempt unknown.
    environment:
      name: ${slug}
      url: \${{ vars.DEPLOY_URL }}
    permissions:
      contents: read
      packages: read
    steps:
      - name: Check the inputs
        env:
          REVISION: \${{ inputs.revision }}
        run: |
          [[ "$REVISION" =~ ^[0-9a-f]{40}$ ]] || { echo "::error::revision must be a full commit SHA"; exit 1; }

      - uses: docker/login-action@v3
        with:
          registry: ghcr.io
          username: \${{ github.actor }}
          password: \${{ secrets.GITHUB_TOKEN }}

      # The image CI published for this revision, pinned by digest.
      - name: Resolve the image
        id: image
        env:
          REVISION: \${{ inputs.revision }}
        run: |
          set -o pipefail
          name="ghcr.io/\${GITHUB_REPOSITORY,,}"
          digest=$(docker buildx imagetools inspect "$name:$REVISION" --format '{{json .Manifest}}' | jq -r .digest) ||
            { echo "::error::No image for $REVISION; CI must publish it first"; exit 1; }
          [[ "$digest" =~ ^sha256:[a-f0-9]{64}$ ]] || { echo "::error::Unexpected digest '$digest'"; exit 1; }
          echo "ref=$name@$digest" >> "$GITHUB_OUTPUT"

${gateStep(environment, true)}
      # On the host, DEPLOY_PATH holds a compose.yaml whose service uses \`image: \${IMAGE}\`.
      - name: Deploy over SSH
        env:
          IMAGE: \${{ steps.image.outputs.ref }}
          SSH_KEY: \${{ secrets.DEPLOY_SSH_KEY }}
          KNOWN_HOSTS: \${{ vars.DEPLOY_KNOWN_HOSTS }}
          HOST: \${{ vars.DEPLOY_HOST }}
          USER_NAME: \${{ vars.DEPLOY_USER }}
          PORT: \${{ vars.DEPLOY_PORT || '22' }}
          DEPLOY_PATH: \${{ vars.DEPLOY_PATH }}
          REGISTRY_TOKEN: \${{ secrets.GITHUB_TOKEN }}
        run: |
          set -euo pipefail
          install -m 700 -d ~/.ssh
          printf '%s\\n' "$SSH_KEY" > ~/.ssh/deploy_key && chmod 600 ~/.ssh/deploy_key
          printf '%s\\n' "$KNOWN_HOSTS" > ~/.ssh/known_hosts
          remote=(ssh -i ~/.ssh/deploy_key -p "$PORT" -o StrictHostKeyChecking=yes -o BatchMode=yes "$USER_NAME@$HOST")
          q() { printf '%q' "$1"; }
          # A Docker config just for this deploy; the registry token travels on stdin, never in a command line.
          config="$DEPLOY_PATH/.deploy-docker-config"
          printf '%s\\n' "$REGISTRY_TOKEN" | "\${remote[@]}" \\
            "umask 077 && mkdir -p $(q "$config") && docker --config $(q "$config") login ghcr.io -u $(q "$GITHUB_ACTOR") --password-stdin >/dev/null"
          "\${remote[@]}" "bash -s -- $(q "$DEPLOY_PATH") $(q "$IMAGE") $(q "$config")" <<'REMOTE'
          set -euo pipefail
          cd "$1"
          trap 'rm -rf "$3"' EXIT
          export DOCKER_CONFIG="$3"
          IMAGE="$2" docker compose pull
          IMAGE="$2" docker compose up -d --wait --wait-timeout 300
          REMOTE

      # The run's result is the attempt's: fail here when the service is not healthy.
      - name: Check the deployed service
        if: vars.DEPLOY_HEALTHCHECK_URL != ''
        env:
          URL: \${{ vars.DEPLOY_HEALTHCHECK_URL }}
        run: curl -fsS --retry 10 --retry-delay 6 --retry-all-errors "$URL"

      - name: Deployment evidence
        env:
          REVISION: \${{ inputs.revision }}
          RELEASE: \${{ inputs.release }}
          ATTEMPT: \${{ inputs.attempt }}
          IMAGE: \${{ steps.image.outputs.ref }}
          ENVIRONMENT: ${JSON.stringify(environment)}
        run: |
          {
            echo "Deployed $REVISION to $ENVIRONMENT for Lixionary Jira $RELEASE $ATTEMPT"
            echo "Image: $IMAGE"
          } >> "$GITHUB_STEP_SUMMARY"
`;

const ciWorkflow = (branch: string) =>
  `# CI for a service that Lixionary Jira deploys. The job names are the check names Jira sees on a commit: set
# each Jira environment's "Required CI checks" to \`images\`, so Proceed waits until the image for the revision exists.
name: CI

on:
  pull_request:
  push:
    branches: [${branch}]

permissions:
  contents: read

concurrency:
  group: ci-\${{ github.ref }}
  cancel-in-progress: \${{ github.event_name == 'pull_request' }}

jobs:
  verify:
    runs-on: ubuntu-24.04
    timeout-minutes: 20
    steps:
      - uses: actions/checkout@v4
      # Replace with the service's own build and tests (mvn verify, npm test, go test ./..., ...).
      - name: Build and test
        run: make test

  # Publishes the image the deploy workflows deploy, tagged with the commit SHA. Only on ${branch}: Jira only deploys
  # revisions on the default branch.
  images:
    needs: verify
    if: github.event_name == 'push' && github.ref == 'refs/heads/${branch}'
    runs-on: ubuntu-24.04
    timeout-minutes: 30
    permissions:
      contents: read
      packages: write
    steps:
      - uses: actions/checkout@v4
      - uses: docker/setup-buildx-action@v3
      - uses: docker/login-action@v3
        with:
          registry: ghcr.io
          username: \${{ github.actor }}
          password: \${{ secrets.GITHUB_TOKEN }}
      - name: Image name
        id: image
        run: echo "name=ghcr.io/\${GITHUB_REPOSITORY,,}" >> "$GITHUB_OUTPUT"
      - uses: docker/build-push-action@v6
        with:
          context: .
          push: true
          tags: \${{ steps.image.outputs.name }}:\${{ github.sha }}
          labels: org.opencontainers.image.revision=\${{ github.sha }}
`;

const COMPOSE_EXAMPLE = `# The host side of the Docker deploy workflows: put this as compose.yaml in DEPLOY_PATH. The workflow sets IMAGE
# to the image CI published for the revision, pinned by digest, and waits for the health check.
services:
  app:
    image: \${IMAGE}
    restart: unless-stopped
    env_file: .env
    healthcheck:
      test: ['CMD', 'wget', '-qO-', 'http://localhost:8080/health']
      interval: 10s
      retries: 6
`;

/** A deploy workflow that runs one script; the deploy workflow template on the environment settings page. */
export const deployWorkflowTemplate = (environment: string) =>
  HEADER('Deploy', environmentSlug(environment)) +
  minimalJob(environment.trim(), environmentSlug(environment));

/** A deploy workflow that runs the revision's image on a host over SSH with Docker Compose. */
export const dockerDeployWorkflow = (environment: string) =>
  HEADER(`Deploy ${environment.trim()}`, environmentSlug(environment)) +
  dockerJob(environment.trim(), environmentSlug(environment));

export interface WorkflowKitEnvironment {
  name: string;
  deployWorkflow: string | null;
  requiresApproval: boolean;
  requiredChecks: string[];
  isCompletionTarget: boolean;
}

export interface WorkflowKitInput {
  project: { key: string; name: string };
  repository: { url: string; defaultBranch: string } | null;
  environments: WorkflowKitEnvironment[];
  /** The Jira server's public URL. */
  jiraUrl: string;
}

export interface KitFile {
  path: string;
  content: string;
}

export interface KitEnvironment {
  name: string;
  /** The workflow file the kit renders for it. */
  workflow: string;
  /** Jira has no deploy workflow for it yet; `workflow` is the kit's suggestion to set. */
  suggested: boolean;
  requiredChecks: string[];
  /** The environment does not require the image check yet. */
  missingImageCheck: boolean;
  requiresApproval: boolean;
  /** The GitHub environment that holds its variables and secrets. */
  githubEnvironment: string;
}

export interface KitSetting {
  name: string;
  kind: 'secret' | 'variable';
  /** Where to set it: an organization-wide value, or each GitHub environment. */
  scope: 'organization' | 'environment';
  required: boolean;
  value: string;
}

export interface WorkflowKit {
  /** Folder name for the ZIP. */
  name: string;
  repository: WorkflowKitInput['repository'];
  files: KitFile[];
  environments: KitEnvironment[];
  settings: KitSetting[];
  /** Self-contained instructions for an AI agent that has the kit's files. */
  prompt: string;
  /** What to ask an agent that has the lixionary-jira plugin. */
  pluginPrompt: string;
}

const kitEnvironments = (input: WorkflowKitInput): KitEnvironment[] => {
  const envs: WorkflowKitEnvironment[] = input.environments.length
    ? input.environments
    : [
        {
          name: 'production',
          deployWorkflow: null,
          requiresApproval: false,
          requiredChecks: [],
          isCompletionTarget: true,
        },
      ];
  const taken = new Set(envs.flatMap((e) => (e.deployWorkflow ? [e.deployWorkflow] : [])));
  return envs.map((e) => {
    let workflow = e.deployWorkflow;
    if (!workflow) {
      const preferred = e.isCompletionTarget ? '.github/workflows/deploy.yml' : null;
      workflow =
        preferred && !taken.has(preferred)
          ? preferred
          : `.github/workflows/deploy-${environmentSlug(e.name)}.yml`;
      taken.add(workflow);
    }
    return {
      name: e.name,
      workflow,
      suggested: !e.deployWorkflow,
      requiredChecks: e.requiredChecks,
      missingImageCheck: !e.requiredChecks.includes(IMAGE_CHECK),
      requiresApproval: e.requiresApproval,
      githubEnvironment: environmentSlug(e.name),
    };
  });
};

const kitSettings = (input: WorkflowKitInput): KitSetting[] => [
  {
    name: 'LIXIONARY_JIRA_CLIENT_ID',
    kind: 'secret',
    scope: 'organization',
    required: true,
    value:
      'Client ID of a service account (Administration → Service accounts → Add service account; no project access)',
  },
  {
    name: 'LIXIONARY_JIRA_CLIENT_SECRET',
    kind: 'secret',
    scope: 'organization',
    required: true,
    value: "That agent's client secret",
  },
  { name: 'JIRA_PROJECT', kind: 'variable', scope: 'organization', required: true, value: input.project.key },
  {
    name: 'LIXIONARY_JIRA_URL',
    kind: 'variable',
    scope: 'organization',
    required: true,
    value: input.jiraUrl,
  },
  {
    name: 'DEPLOY_SSH_KEY',
    kind: 'secret',
    scope: 'environment',
    required: true,
    value: 'Private key of a deploy-only account on the host (no forwarding or PTY in authorized_keys)',
  },
  { name: 'DEPLOY_HOST', kind: 'variable', scope: 'environment', required: true, value: 'Host name' },
  {
    name: 'DEPLOY_USER',
    kind: 'variable',
    scope: 'environment',
    required: true,
    value: 'Account that runs Docker',
  },
  {
    name: 'DEPLOY_PATH',
    kind: 'variable',
    scope: 'environment',
    required: true,
    value: 'Directory on the host with the compose.yaml (see deploy/compose.example.yaml)',
  },
  {
    name: 'DEPLOY_KNOWN_HOSTS',
    kind: 'variable',
    scope: 'environment',
    required: true,
    value: 'Output of ssh-keyscan -p <port> <host>, checked strictly',
  },
  {
    name: 'DEPLOY_PORT',
    kind: 'variable',
    scope: 'environment',
    required: false,
    value: 'SSH port, default 22',
  },
  {
    name: 'DEPLOY_URL',
    kind: 'variable',
    scope: 'environment',
    required: false,
    value: 'Public URL shown on the GitHub deployment',
  },
  {
    name: 'DEPLOY_HEALTHCHECK_URL',
    kind: 'variable',
    scope: 'environment',
    required: false,
    value: 'URL that must answer 2xx after the deploy, or the run fails',
  },
];

const table = (head: string[], rows: string[][]) =>
  [
    `| ${head.join(' | ')} |`,
    `| ${head.map(() => '---').join(' | ')} |`,
    ...rows.map((r) => `| ${r.join(' | ')} |`),
  ].join('\n');

function kitReadme(input: WorkflowKitInput, envs: KitEnvironment[], settings: KitSetting[]) {
  const { key, name } = input.project;
  const branch = input.repository?.defaultBranch ?? 'main';
  const todo = envs.flatMap((e) => [
    ...(e.suggested ? [`Set **${e.name}**'s deploy workflow to \`${e.workflow}\`.`] : []),
    ...(e.missingImageCheck ? [`Add \`${IMAGE_CHECK}\` to **${e.name}**'s required CI checks.`] : []),
  ]);
  return `# GitHub workflows for ${name} (${key})

Lixionary Jira deploys a release of ${key} by running a GitHub workflow in the service's repository, once every
included ticket's pull request is merged into \`${branch}\` and CI passed on it. This kit holds those workflows,
filled in for ${key}, and how to set them up. Ask an AI agent to adapt them to the repository with
[AGENT_PROMPT.md](AGENT_PROMPT.md).

Repository: ${input.repository ? `${input.repository.url} (\`${branch}\`)` : '**not set**: set it in Jira under Project → Settings → Repository and builders.'}

## Files

${table(
  ['File', 'Purpose'],
  [
    [
      '`.github/workflows/ci.yml`',
      `Test every change; on \`${branch}\`, publish an image tagged with the commit SHA (job \`${IMAGE_CHECK}\`)`,
    ],
    ...envs.map((e) => [
      `\`${e.workflow}\``,
      `Deploy that image to **${e.name}** over SSH with Docker Compose`,
    ]),
    [
      '`alternatives/deploy-minimal.yml`',
      'A deploy workflow that runs one script, for services without a container image',
    ],
    ['`deploy/compose.example.yaml`', 'The compose.yaml the Docker deploy expects on the host'],
  ],
)}

## 1. Lixionary Jira (an admin)

${table(
  ['Environment', 'Deploy workflow', 'Required CI checks', 'Release approval'],
  envs.map((e) => [
    e.name,
    e.suggested ? `not set: set \`${e.workflow}\`` : `\`${e.workflow}\``,
    e.requiredChecks.length
      ? e.requiredChecks.map((c) => `\`${c}\``).join(', ')
      : `none: add \`${IMAGE_CHECK}\``,
    e.requiresApproval ? 'required' : 'not required',
  ]),
)}
${todo.length ? `\nStill to do in **Project → Settings → Environments**:\n\n${todo.map((t) => `- ${t}`).join('\n')}\n` : ''}
Then choose **Check workflow on GitHub** on each environment once the workflows are on \`${branch}\`.

The deploy gate needs a service account: **Administration → Service accounts → Add service account** (for example
\`github-deploy-gate\`), then issue a credential under **Credentials & setup**. It only reads releases, which every
service account may do, so leave **Project access** empty. One service account can serve every repository.

## 2. GitHub (a GitHub admin)

1. Jira's GitHub App is installed on the repository with *Contents: Read and write*, *Pull requests:
   Read-only*, *Checks: Read-only*, *Commit statuses: Read-only* and *Actions: Read and write*, and its webhook
   sends *Workflow run* and *Pull request* events.
2. The deploy workflows use \`${GATE_ACTION}\`. That repository is private: in its
   **Settings → Actions → General → Access**, choose *Accessible from repositories in the organization*. This
   is needed once for the organization, not for each service.
3. Branch protection on \`${branch}\`: require a pull request review and the \`verify\` check. A merged pull
   request is the human gate before a deployment.
4. One GitHub environment per Jira environment (**Settings → Environments**): ${envs.map((e) => `\`${e.githubEnvironment}\``).join(', ')}.
   Leave *Required reviewers* off: Jira already gated the release, and a run that waits for a reviewer for more
   than an hour marks the attempt unknown.

## 3. Secrets and variables

Organization-scoped values can be organization secrets and variables shared by every service, or set on each
GitHub environment. Environment-scoped values are set on each GitHub environment above.

${table(
  ['Name', 'Kind', 'Scope', 'Value'],
  settings.map((s) => [`\`${s.name}\``, s.kind, s.scope, `${s.required ? '' : 'Optional. '}${s.value}`]),
)}

\`GITHUB_TOKEN\` needs no setup: CI pushes the image with it and the deploy job pulls the image with it. For
\`alternatives/deploy-minimal.yml\`, add whatever secrets its deploy script needs instead of the \`DEPLOY_*\` ones.

## 4. The host

\`DEPLOY_PATH\` holds a \`compose.yaml\` like \`deploy/compose.example.yaml\`: its service takes the image from
\`IMAGE\`, and \`docker compose up --wait\` waits for its health check. Keep runtime configuration (\`.env\`) on
the host. The deploy user needs Docker and write access to \`DEPLOY_PATH\`.

## 5. Try it

1. Merge the workflows to \`${branch}\` and wait for CI: \`${IMAGE_CHECK}\` must pass on the new commit.
2. In Jira, **Check workflow on GitHub** on each environment.
3. Take a ticket through review and merge, create a release for it, and **Proceed**. The run appears in GitHub
   Actions as \`Deploy REL-n ATT-n <sha>\`, and its result appears on the attempt.

A run started by hand fails at the gate. That is expected: deployments start from Jira.
`;
}

function kitPrompt(input: WorkflowKitInput, envs: KitEnvironment[]) {
  const { key, name } = input.project;
  const branch = input.repository?.defaultBranch ?? 'main';
  return `Set up the GitHub workflows that let Lixionary Jira deploy ${name} (${key}).

Repository: ${input.repository ? `${input.repository.url}, default branch \`${branch}\`` : 'not configured in Jira yet; work in the repository you have checked out'}.
You have the workflow kit for ${key}: README.md (setup and settings), .github/workflows/ (CI and one deploy
workflow per environment), alternatives/deploy-minimal.yml and deploy/compose.example.yaml.

## What to do
1. Check that your checkout is this repository. Inspect how the service builds, tests and runs today: language and
   build tool, existing workflows in .github/workflows, any Dockerfile, how it is deployed now.
2. Create a branch, for example \`chore/jira-deploy-workflows\`.
3. CI: make \`.github/workflows/ci.yml\` fit the stack. Replace the \`verify\` job's \`make test\` with the real build
   and tests, keeping any setup (language version, caches, services) the existing CI has. If CI already exists,
   merge into it rather than replacing it, and keep its other jobs.
4. Image: the \`${IMAGE_CHECK}\` job builds the repository's Dockerfile. If there is none, add a production
   Dockerfile (multi-stage, non-root user, a health endpoint if the service has one) and a .dockerignore.
5. Deploy: copy each deploy workflow to its path (${envs.map((e) => `\`${e.workflow}\` for ${e.name}`).join(', ')}).
   If the service does not run as a container on a host, start from alternatives/deploy-minimal.yml instead and
   write its deploy step for how the service really deploys.
6. Add deploy/compose.example.yaml adapted to the service (port, health check, volumes) as documentation for the
   host, unless the service deploys another way.
7. Run any workflow linter you have (actionlint), commit, push, and open a pull request. Never merge it yourself.

## Keep these, or the deployment will not work
- \`on: workflow_dispatch\` with exactly the string inputs \`revision\`, \`release\` and \`attempt\`.
- \`run-name: Deploy \${{ inputs.release }} \${{ inputs.attempt }} \${{ inputs.revision }}\`: Jira reads the run by its name.
- The "Check the attempt with Lixionary Jira" step (\`${GATE_ACTION}\`) runs before anything changes, with the
  Jira environment's exact name: ${envs.map((e) => JSON.stringify(e.name)).join(', ')}.
- Deploy exactly \`inputs.revision\`, and fail the run when the deployment or its checks fail: the run's result
  is the attempt's outcome.
- Keep the job name \`${IMAGE_CHECK}\` (Jira waits for it before deploying). If you rename it, tell the admin to set
  the new name as the environments' required CI check.
- Pass inputs to scripts through \`env\`, never by interpolating them into \`run\`.

## Never
- Put secret values in files. Workflows read them from \`secrets.*\` and \`vars.*\`.
- Merge the pull request, deploy, or run the deploy workflow by hand.

## When you are done
Reply with the pull request link and a checklist for the admin, from README.md: the Jira settings for each
environment, the GitHub App and access settings, branch protection, and each secret and variable to set, with
the values you know (JIRA_PROJECT is ${key}; LIXIONARY_JIRA_URL is ${input.jiraUrl}). Mention anything you
could not verify.
`;
}

/** Renders the kit for a project. */
export function buildWorkflowKit(input: WorkflowKitInput): WorkflowKit {
  const envs = kitEnvironments(input);
  const settings = kitSettings(input);
  const prompt = kitPrompt(input, envs);
  const branch = input.repository?.defaultBranch ?? 'main';
  return {
    name: `${input.project.key.toLowerCase()}-github-workflows`,
    repository: input.repository,
    files: [
      { path: 'README.md', content: kitReadme(input, envs, settings) },
      { path: 'AGENT_PROMPT.md', content: prompt },
      { path: '.github/workflows/ci.yml', content: ciWorkflow(branch) },
      ...envs.map((e) => ({ path: e.workflow, content: dockerDeployWorkflow(e.name) })),
      { path: 'alternatives/deploy-minimal.yml', content: deployWorkflowTemplate(envs[0]!.name) },
      { path: 'deploy/compose.example.yaml', content: COMPOSE_EXAMPLE },
    ],
    environments: envs,
    settings,
    prompt,
    pluginPrompt: `/lixionary-jira:workflows ${input.project.key}`,
  };
}
